/**
 * The shape library: named sets of vector glyphs that the Stamp module draws
 * along a stroke. Sets come from built-ins, SVG import, or capture-to-set, and
 * user sets persist in their own IndexedDB store (surviving Clear / New).
 */
import type { PathData, PathSegment, Shape } from './scene';
import { bbox, circlePath, transformPath } from './geometry';
import { parseSvgToGlyphs } from './svg-import';
import type { GlyphSource } from './svg-import';
import { LIBRARY_STORE, openAlchemyDb } from './session';
import { uid } from '../util/id';

export interface ShapeItem {
  id: string;
  path: PathData; // centred at origin, longest side normalised to 1
  w: number; // normalised width (<= 1)
  h: number; // normalised height (<= 1)
  stroke?: boolean; // open/stroke-only glyph → stamp as a stroke, not a fill
  fillRule?: CanvasFillRule;
}

export interface ShapeSet {
  id: string;
  name: string;
  builtin: boolean;
  items: ShapeItem[];
}

/** Centre a glyph at the origin and scale its longest side to 1. */
export function normalizeGlyph(path: PathData): { path: PathData; w: number; h: number } | null {
  const b = bbox(path);
  const cw = b.maxX - b.minX;
  const ch = b.maxY - b.minY;
  const longest = Math.max(cw, ch);
  if (longest < 1e-6) return null;
  const cx = (b.minX + b.maxX) / 2;
  const cy = (b.minY + b.maxY) / 2;
  const scale = 1 / longest;
  const np = transformPath(path, (p) => ({ x: (p.x - cx) * scale, y: (p.y - cy) * scale }));
  return { path: np, w: cw * scale, h: ch * scale };
}

function cleanName(name: string): string {
  const n = name.replace(/\.svg$/i, '').trim();
  return n || 'Imported';
}

export class ShapeLibrary {
  sets: ShapeSet[];
  onChange?: () => void;
  onError?: (msg: string) => void;
  private saveTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.sets = builtinSets();
  }

  list(): { value: string; label: string }[] {
    return this.sets.map((s) => ({ value: s.id, label: `${s.name} · ${s.items.length}` }));
  }

  get(id: string): ShapeSet | undefined {
    return this.sets.find((s) => s.id === id);
  }

  firstId(): string {
    return this.sets[0]?.id ?? '';
  }

  /** Import an SVG file's geometry as a new named set. Returns the new set id. */
  addFromSvg(svgText: string, name: string): string | null {
    let glyphs: GlyphSource[];
    try {
      glyphs = parseSvgToGlyphs(svgText);
    } catch (e) {
      this.onError?.(e instanceof Error ? e.message : 'Could not read that SVG');
      return null;
    }
    const items = glyphsToItems(glyphs);
    if (items.length === 0) {
      this.onError?.('No shapes found in that SVG');
      return null;
    }
    const set: ShapeSet = { id: uid(), name: cleanName(name), builtin: false, items };
    this.sets.push(set);
    this.save();
    this.onChange?.();
    return set.id;
  }

  /** Promote already-committed scene shapes into a library set (a library edit, not a canvas edit). */
  captureFromShapes(shapes: Shape[], setId?: string): string | null {
    const items: ShapeItem[] = [];
    for (const s of shapes) {
      const n = normalizeGlyph(s.path);
      if (!n) continue;
      items.push({ id: uid(), path: n.path, w: n.w, h: n.h, stroke: s.style.mode === 'stroke', fillRule: s.style.fillRule });
    }
    if (items.length === 0) return null;
    let set = setId ? this.get(setId) : undefined;
    if (!set || set.builtin) set = this.sets.find((x) => !x.builtin && x.name === 'My shapes');
    if (!set) {
      set = { id: uid(), name: 'My shapes', builtin: false, items: [] };
      this.sets.push(set);
    }
    set.items.push(...items);
    this.save();
    this.onChange?.();
    return set.id;
  }

  renameSet(id: string, name: string): void {
    const s = this.get(id);
    if (s && !s.builtin) {
      s.name = cleanName(name);
      this.save();
      this.onChange?.();
    }
  }

  deleteSet(id: string): void {
    const s = this.get(id);
    if (!s || s.builtin) return;
    this.sets = this.sets.filter((x) => x.id !== id);
    this.save();
    this.onChange?.();
  }

  async load(): Promise<void> {
    const db = await openAlchemyDb();
    if (!db) return;
    const stored = await new Promise<unknown>((resolve) => {
      try {
        const tx = db.transaction(LIBRARY_STORE, 'readonly');
        const req = tx.objectStore(LIBRARY_STORE).get('sets');
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
    if (Array.isArray(stored)) {
      const userSets = (stored as ShapeSet[]).filter((s) => s && Array.isArray(s.items));
      if (userSets.length) {
        this.sets = [...builtinSets(), ...userSets.map((s) => ({ ...s, builtin: false }))];
        this.onChange?.();
      }
    }
  }

  save(): void {
    if (this.saveTimer !== null) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => void this.writeNow(), 400);
  }

  private async writeNow(): Promise<void> {
    const db = await openAlchemyDb();
    if (!db) return;
    const userSets = this.sets.filter((s) => !s.builtin);
    try {
      const tx = db.transaction(LIBRARY_STORE, 'readwrite');
      const req = tx.objectStore(LIBRARY_STORE).put(userSets, 'sets');
      req.onerror = () => this.onError?.('Could not save shape sets — storage may be full');
      tx.onabort = () => this.onError?.('Could not save shape sets — storage may be full');
    } catch {
      this.onError?.('Could not save shape sets');
    }
  }
}

function glyphsToItems(glyphs: GlyphSource[]): ShapeItem[] {
  const items: ShapeItem[] = [];
  for (const g of glyphs) {
    const n = normalizeGlyph(g.path);
    if (!n) continue;
    items.push({ id: uid(), path: n.path, w: n.w, h: n.h, stroke: g.strokeOnly, fillRule: g.fillRule });
  }
  return items;
}

function starPath(outer: number, inner: number, points: number): PathData {
  const segs: PathSegment[] = [];
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = (Math.PI * i) / points - Math.PI / 2;
    const p = { x: Math.cos(a) * r, y: Math.sin(a) * r };
    segs.push(i === 0 ? { t: 'M', p } : { t: 'L', p });
  }
  segs.push({ t: 'Z' });
  return { segments: segs };
}

function builtinSets(): ShapeSet[] {
  const item = (path: PathData): ShapeItem => {
    const n = normalizeGlyph(path) ?? { path, w: 1, h: 1 };
    return { id: uid(), path: n.path, w: n.w, h: n.h };
  };
  const square: PathData = {
    segments: [
      { t: 'M', p: { x: -50, y: -50 } },
      { t: 'L', p: { x: 50, y: -50 } },
      { t: 'L', p: { x: 50, y: 50 } },
      { t: 'L', p: { x: -50, y: 50 } },
      { t: 'Z' },
    ],
  };
  const triangle: PathData = {
    segments: [
      { t: 'M', p: { x: 0, y: -55 } },
      { t: 'L', p: { x: 48, y: 40 } },
      { t: 'L', p: { x: -48, y: 40 } },
      { t: 'Z' },
    ],
  };
  const diamond: PathData = {
    segments: [
      { t: 'M', p: { x: 0, y: -50 } },
      { t: 'L', p: { x: 40, y: 0 } },
      { t: 'L', p: { x: 0, y: 50 } },
      { t: 'L', p: { x: -40, y: 0 } },
      { t: 'Z' },
    ],
  };
  return [
    {
      // Stable id so firstId() is invariant across constructor and load() —
      // otherwise the Stamp 'set' default would go stale after a reload.
      id: 'builtin-basics',
      name: 'Basics',
      builtin: true,
      items: [item(circlePath({ x: 0, y: 0 }, 50)), item(square), item(triangle), item(diamond), item(starPath(50, 20, 5))],
    },
  ];
}
